import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { PasswordStrengthMeter, FieldError } from "@/components/FormFeedback";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { validatePassword, validateConfirmPassword } from "@/lib/validation";

export default function PasswordChangeModal({ open, onOpenChange, forced = false, onSuccess }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [touched, setTouched] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fieldErrors = {
    newPassword: validatePassword(newPassword),
    confirmPassword: validateConfirmPassword(newPassword, confirmPassword),
  };
  const isValid = currentPassword.length > 0 && Object.values(fieldErrors).every((e) => !e);

  const submit = async () => {
    setTouched({ newPassword: true, confirmPassword: true });
    setError("");
    if (!currentPassword) {
      setError("Current password is required");
      return;
    }
    if (!isValid) return;
    setLoading(true);
    try {
      await api.put("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password updated successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTouched({});
      onSuccess && onSuccess();
      onOpenChange(false);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={forced ? () => {} : onOpenChange}>
      <DialogContent data-testid="password-change-modal" className="rounded-xl">
        <DialogHeader>
          <DialogTitle>{forced ? "Set a New Password" : "Reset Password"}</DialogTitle>
          <DialogDescription>
            {forced ? "For security, you must change your temporary password before continuing." : "Enter your current password and choose a new one."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="current-password">Current Password</Label>
            <PasswordInput
              testId="current-password-input"
              id="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="new-password">New Password</Label>
            <PasswordInput
              testId="new-password-input"
              id="new-password"
              value={newPassword}
              onBlur={() => setTouched((t) => ({ ...t, newPassword: true }))}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <PasswordStrengthMeter password={newPassword} />
            {touched.newPassword && <FieldError testId="new-password-error" message={fieldErrors.newPassword} />}
          </div>
          <div>
            <Label htmlFor="confirm-password">Re-type New Password</Label>
            <PasswordInput
              testId="confirm-password-input"
              id="confirm-password"
              value={confirmPassword}
              onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {touched.confirmPassword && <FieldError testId="confirm-password-error" message={fieldErrors.confirmPassword} />}
          </div>
          {error && (
            <p data-testid="password-change-error" className="text-sm text-red-600">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          {!forced && (
            <Button variant="outline" data-testid="password-change-cancel-btn" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          )}
          <Button
            data-testid="password-change-submit-btn"
            className="bg-teal-700 hover:bg-teal-800"
            onClick={submit}
            disabled={loading || !isValid}
          >
            {loading ? "Updating..." : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
